import React from "react";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Breadcrumbs, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";

export interface AppBreadcrumbItem {
  label: string;
  to?: string;
}

interface AppBreadcrumbsProps {
  items: AppBreadcrumbItem[];
  /**
   * Compact variant for the shell topbar: removes the page-level bottom
   * margin and tightens the separator so breadcrumbs fit the topbar center
   * area. Defaults to `false` (page-level usage with `mb: 2`).
   */
  compact?: boolean;
}

export function AppBreadcrumbs({
  items,
  compact = false,
}: AppBreadcrumbsProps): React.ReactElement {
  return (
    <Breadcrumbs
      separator={
        <NavigateNextIcon
          fontSize="small"
          sx={{ fontSize: compact ? "0.9rem" : undefined, opacity: 0.5 }}
        />
      }
      sx={{
        mb: compact ? 0 : 2,
        "& .MuiBreadcrumbs-ol": {
          flexWrap: "nowrap",
          overflow: "hidden",
          ...(compact ? { my: 0 } : {}),
        },
        "& .MuiBreadcrumbs-li": {
          minWidth: 0,
        },
        "& .MuiBreadcrumbs-separator": {
          mx: compact ? 0.5 : undefined,
        },
      }}
    >
      {items.map((item, index) => {
        const href = item.to;
        if (index === items.length - 1 || !href) {
          return (
            <Typography
              key={`${item.label}-${index}`}
              color="text.primary"
              variant="body2"
              noWrap
            >
              {item.label}
            </Typography>
          );
        }

        return (
          <Link
            key={`${item.label}-${index}`}
            component={RouterLink}
            to={href}
            color="inherit"
            variant="body2"
            underline="hover"
            sx={{ minWidth: 0 }}
          >
            {item.label}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
}
